import {
  resolvePalette,
  THEMES,
  TOKEN_VARS,
  type Branding,
  type ThemeName,
  type ThemePalette,
} from '@lpa/protocol';

import { resolveFamilyOverrides, type FamilyClash } from './families.js';

/**
 * The branded palette, as the one stylesheet the app actually renders from.
 *
 * **Why a stylesheet rather than inline styles.** Everything already reads
 * `var(--accent)`, `var(--fam-verbs-mark)` and the rest; the single source of
 * truth the white label needs is therefore a single place those variables are
 * *defined*, not a second styling mechanism running alongside the first. One
 * `<style>` element appended to the head, redefining the same custom properties
 * `global.css` sets, means no component knows branding exists and none can
 * opt out of it.
 *
 * It is written as text rather than through `style.setProperty` for one reason
 * that matters: `prefers-color-scheme`. The dark palette has to be inside a
 * media query, and there is no imperative API for "set this variable, but only
 * in dark mode". The alternative is reading the media query in JavaScript and
 * rewriting everything when it changes -- a second, worse implementation of a
 * thing CSS already does correctly, including for a device that flips at sunset
 * while a lesson is running.
 */

const THEME_COMMENT: Record<ThemeName, string> = {
  light: 'light',
  dark: 'dark (prefers-color-scheme)',
};

function declarations(palette: ThemePalette, families: string[]): string[] {
  const lines = Object.entries(TOKEN_VARS).map(
    ([token, cssVar]) => `${cssVar}: ${palette[token as keyof ThemePalette]};`,
  );

  // The focus ring is the accent, spelled as a whole `outline` shorthand in
  // `global.css`. Rebuilt here rather than left behind: a rebranded app whose
  // focus ring stayed the shipped blue would fail its own palette on the one
  // thing D-20 is least willing to lose.
  lines.push(`--focus: 3px solid ${palette.accent};`);

  return [...lines, ...families];
}

function block(selector: string, lines: string[], indent: string): string {
  return `${indent}${selector} {\n${lines.map((l) => `${indent}  ${l}`).join('\n')}\n${indent}}`;
}

export interface BrandingStylesheet {
  css: string;
  /** Families displaced by the core colour, for the settings screen to report. */
  clashes: FamilyClash[];
}

export function brandingStylesheet(branding: Branding): BrandingStylesheet {
  const palettes: Record<ThemeName, ThemePalette> = {
    light: resolvePalette(branding, 'light'),
    dark: resolvePalette(branding, 'dark'),
  };

  const { clashes, colors } = resolveFamilyOverrides(branding.core, palettes);

  // Only the displaced families are emitted. Everything else keeps the
  // hand-tuned values in `families.css`, which is what makes an unbranded
  // install byte-identical to the one that shipped.
  const familyLines = (theme: ThemeName): string[] =>
    Object.entries(colors[theme]).flatMap(([family, trio]) => [
      `--fam-${family}-tint: ${trio.tint};`,
      `--fam-${family}-ink: ${trio.ink};`,
      `--fam-${family}-mark: ${trio.mark};`,
    ]);

  const parts = [
    '/* Generated from the branding record. Edited in /admin/settings. */',
    `/* ${THEME_COMMENT.light} */`,
    block(':root', declarations(palettes.light, familyLines('light')), ''),
    `/* ${THEME_COMMENT.dark} */`,
    '@media (prefers-color-scheme: dark) {',
    block(':root', declarations(palettes.dark, familyLines('dark')), '  '),
    '}',
  ];

  return { css: parts.join('\n'), clashes };
}

/** Every theme's resolved palette, for a preview that shows both at once. */
export function resolveAll(branding: Branding): Record<ThemeName, ThemePalette> {
  return Object.fromEntries(
    THEMES.map((theme) => [theme, resolvePalette(branding, theme)]),
  ) as Record<ThemeName, ThemePalette>;
}
